import {
  conversationListResponseSchema,
  conversationResponseSchema,
  type ConversationListResponse,
  type CreateConversationRequest,
} from '@coworker/contracts';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { useAuth } from '../auth/AuthProvider.js';
import { useRecoverSession } from './use-recover-session.js';
import { ApiError } from '../lib/api-error.js';
import { useCurrentWorkspace } from './use-current-workspace.js';

/**
 * The caller's own chats - API-008, WEB-007.
 *
 * **What the wire calls a conversation is a thread**, and a thread is private to
 * whoever opened it: `GET /v1/conversations` filters on the caller, so this is
 * never "the workspace's chats" even though the workspace scopes the request.
 * That is why the key carries the workspace id and not a user id - one session
 * is one user, and switching workspaces is the only thing that changes the
 * answer.
 */
export const conversationsQueryKey = (workspaceId: string | undefined) =>
  ['conversations', workspaceId] as const;

export interface ConversationsState {
  readonly query: UseQueryResult<ConversationListResponse, unknown>;
  readonly isUnauthenticated: boolean;
}

export function useConversations(): ConversationsState {
  const { api, status } = useAuth();
  const workspace = useCurrentWorkspace();

  const query = useQuery({
    queryKey: conversationsQueryKey(workspace?.id),
    // Gated on the workspace as well as the session, for the reason
    // `use-employees.ts` gives: without an id the request goes out with no
    // `x-workspace-id` and comes back 400, which reads as a failure when the
    // truth is that there is nothing to ask about yet.
    enabled: status === 'signedIn' && workspace !== undefined,
    queryFn: () =>
      api.request('/v1/conversations', {
        schema: conversationListResponseSchema,
        workspaceId: workspace?.id ?? '',
      }),
  });

  const isUnauthenticated = query.error instanceof ApiError && query.error.isUnauthenticated;

  // A lapsed token on this screen is recovered here, not only in `AppShell`.
  // This value was computed and returned and read by nobody, so a 401 on this
  // query dead-ended on an error card whose retry re-sent the same token.
  useRecoverSession({ isUnauthenticated, erroredAt: query.errorUpdatedAt });

  return {
    query,
    isUnauthenticated,
  };
}

/**
 * Opens a chat - EMP-009.
 *
 * `employeeId` is optional on the wire and the API resolves the Generalist when
 * it is absent. The screen sends it explicitly anyway, so that what a person
 * picked is what was recorded even if the default ever changes underneath them.
 */
export function useCreateConversation() {
  const { api } = useAuth();
  const workspace = useCurrentWorkspace();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateConversationRequest) =>
      api.request('/v1/conversations', {
        method: 'POST',
        schema: conversationResponseSchema,
        workspaceId: workspace?.id ?? '',
        body: input,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: conversationsQueryKey(workspace?.id) }),
  });
}
